/**
 * @fileoverview Task Create Hook
 *
 * Hook for creating new tasks via the AutomationCreateModal.
 * Manages task configuration state and the multi-step creation flow.
 *
 * FLOW:
 * 1. User chooses method (blank / clone / template)
 * 2. Hook initialises a taskConfig accordingly
 * 3. User optionally edits config via category editor
 * 4. createTask() converts config to payload and calls the API
 */

import { useState, useCallback } from "react";
import cloudApi from "../../../../services/cloudApi";
import { buildDefaultTaskConfig } from "../utils/configHelpers";
import {
  convertTaskToTaskConfig,
  convertTaskConfigToCreatePayload,
} from "../utils/taskConfigConverters";
import { getTemplateById } from "../config/templates";
import { getDemoById } from "../config/demos";

// Steps in the creation flow
export const STEPS = {
  METHOD: "method",         // choose blank / clone / template
  PICK_TYPE: "pickType",    // choose automation type (blank path)
  PICK_CLONE: "pickClone",  // choose source automation (clone path)
  TEMPLATE_FORM: "templateForm", // fill in template fields
  PREVIEW: "preview",       // simplified preview before running (demo path)
  CONFIGURE: "configure",   // full category editor
};

const useTaskCreate = ({ onCreated } = {}) => {
  const [step, setStep] = useState(STEPS.METHOD);
  const [taskConfig, setTaskConfig] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // -------------------------------------------------------------------
  // Initialisation helpers
  // -------------------------------------------------------------------

  /** Start a blank automation of the given type */
  const initBlank = useCallback((automationType) => {
    const config = buildDefaultTaskConfig(automationType);
    config.general = { ...config.general, name: "" };
    setTaskConfig(config);
    setStep(STEPS.CONFIGURE);
  }, []);

  /** Clone from an existing task (fetches full task from API) */
  const initFromClone = useCallback(async (taskId) => {
    try {
      setCreateError(null);
      const response = await cloudApi.getTask(taskId);
      const taskData = response.task || response;
      const config = convertTaskToTaskConfig(taskData);
      // Clear identity fields so it becomes a new task
      config.general = {
        ...config.general,
        name: `${config.general?.name || "Untitled"} (copy)`,
        createdAt: null,
        updatedAt: null,
      };
      config.metadata = {
        ...config.metadata,
        title: config.general.name,
      };
      setTaskConfig(config);
      setStep(STEPS.CONFIGURE);
    } catch (error) {
      setCreateError(error.message || "Failed to load automation for cloning");
    }
  }, []);

  /** Initialise from a template with user inputs and go to template form */
  const selectTemplate = useCallback((templateId) => {
    const template = getTemplateById(templateId);
    if (!template) {
      setCreateError(`Template "${templateId}" not found`);
      return;
    }
    setSelectedTemplate(template);
    setStep(STEPS.TEMPLATE_FORM);
  }, []);

  /** Build a taskConfig from a demo automation. Returns the config or null on error. */
  const initFromDemo = useCallback((demoId) => {
    const demo = getDemoById(demoId);
    if (!demo) {
      setCreateError(`Demo "${demoId}" not found`);
      return null;
    }
    const config = convertTaskToTaskConfig(demo.task);
    config.general.name = demo.name;
    config.metadata.title = demo.name;
    config.general.createdAt = null;
    config.general.updatedAt = null;
    setTaskConfig(config);
    return config;
  }, []);

  /** Build taskConfig from template fields and optionally open the full editor */
  const applyTemplateInputs = useCallback(
    (inputs) => {
      if (!selectedTemplate) return;
      const config = selectedTemplate.buildTaskConfig(inputs);
      setTaskConfig(config);
      return config;
    },
    [selectedTemplate]
  );

  // -------------------------------------------------------------------
  // Config editing (same API as useTaskEdit)
  // -------------------------------------------------------------------

  const updateTaskSection = useCallback((sectionKey, value) => {
    setTaskConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, [sectionKey]: value };
    });
    setCreateError(null);
  }, []);

  // -------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------

  const goToStep = useCallback((newStep) => setStep(newStep), []);

  const reset = useCallback(() => {
    setStep(STEPS.METHOD);
    setTaskConfig(null);
    setCreateError(null);
    setSelectedTemplate(null);
    setIsCreating(false);
  }, []);

  // -------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------

  const createTask = useCallback(
    async (runImmediately = false, configOverride = null) => {
      const config = configOverride || taskConfig;
      if (!config) {
        setCreateError("No configuration to create");
        return null;
      }
      if (isCreating) return null;

      // Basic validation: name is required
      const name = config.general?.name?.trim() || config.metadata?.title?.trim();
      if (!name) {
        setCreateError("Automation name is required");
        return null;
      }

      setIsCreating(true);
      setCreateError(null);

      try {
        const payload = convertTaskConfigToCreatePayload(config, { runImmediately });
        const result = await cloudApi.createTask(payload);

        if (onCreated) {
          onCreated(result);
        }
        return result;
      } catch (error) {
        const message = error?.message || "Failed to create automation. Please try again.";
        setCreateError(message);
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [taskConfig, isCreating, onCreated]
  );

  return {
    // State
    step,
    taskConfig,
    isCreating,
    createError,
    selectedTemplate,

    // Init actions
    initBlank,
    initFromClone,
    initFromDemo,
    selectTemplate,
    applyTemplateInputs,

    // Edit actions
    updateTaskSection,
    setTaskConfig,

    // Navigation
    goToStep,
    reset,

    // Create
    createTask,
  };
};

export default useTaskCreate;
